import React, { useState, useRef } from 'react';
import Styles from '../../assets/css/auth/otpVerify.module.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import OtpVerifyLogo from '../../assets/images/logo/Otp_verify.png';
import { IoArrowBack } from "react-icons/io5";
import { adminOtpVerify } from '../../services/Api';
import { AiOutlineLoading } from 'react-icons/ai';
import { useDispatch } from 'react-redux';
import { popUpBoxTrigger } from "../../features/message/popUpSlice";
import PopUpMessage from "../../utils/errors/PopUpMessage";
import { responseHandler } from '../../utils/responseHandler';

const OtpVerify = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [otp, setOtp] = useState(["", "", "", ""]);
    const [error, setError] = useState('');
    const inputRefs = useRef([]);
    
    // Get email from navigation state
    const email = location.state?.email || '';

    const showPopup = (type, heading, message) => {
        dispatch(popUpBoxTrigger({
            type,
            heading,
            message,
            show: true
        }));
    };

    const validateOtp = (otpArray) => {
        const otpString = otpArray.join("");
        if (otpString.length === 0) {
            return "OTP is required";
        }
        if (otpString.length !== 4) {
            return "Please enter all 4 digits of the OTP";
        }
        if (!/^\d+$/.test(otpString)) {
            return "OTP must contain only numbers";
        }
        return "";
    };

    const handleChange = (index, event) => {
        const value = event.target.value.replace(/\D/g, "");
        if (value.length > 1) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setError('');

        if (value && index < 3) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (index, event) => {
        if (event.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handleOtpVerify = async (event) => {
        event.preventDefault();
        
        // Validate OTP
        const otpError = validateOtp(otp);
        if (otpError) {
            setError(otpError);
            return;
        }

        setLoading(true);
        try {
            const otpVerify = {
                email: email,
                otp: otp.join("")
            };

            const response = await adminOtpVerify(otpVerify);
            const result = await responseHandler(response);

            if (result.status === 200) {
                showPopup('success', 'OTP Verified', 'Your OTP has been verified successfully.');
                await new Promise(resolve => setTimeout(resolve, 1000));
                navigate("/newPassword", { state: { email } });
            } else {
                const errorMessage = result.message || 'Invalid OTP. Please try again.';
                showPopup('error', 'Verification Failed', errorMessage);
                // Clear OTP fields on error
                setOtp(["", "", "", ""]);
                inputRefs.current[0].focus();
            }
        } catch (error) {

            if (!navigator.onLine) {
                showPopup('error', 'Connection Error', 'Please check your internet connection.');
            } else if (error.response) {
                const status = error.response.status;
                switch (status) {
                    case 400:
                        showPopup('error', 'Invalid OTP', 'The OTP you entered is incorrect. Please try again.');
                        break;
                    case 401:
                        showPopup('error', 'OTP Expired', 'Your OTP has expired. Please request a new one.');
                        break;
                    case 429:
                        showPopup('error', 'Too Many Attempts', 'Too many failed attempts. Please try again later.');
                        break;
                    default:
                        showPopup('error', 'Error', error.response.data.message || 'Failed to verify OTP.');
                }
                // Clear OTP fields on error
                setOtp(["", "", "", ""]);
                inputRefs.current[0].focus();
            } else {
                showPopup('error', 'Error', 'Unable to connect to the server.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`${Styles.container}`}>
            <PopUpMessage />
            
            <div className={`${Styles.otpBox}`}>
                <img src={OtpVerifyLogo} alt="otp verify" />
                <h2 className={`${Styles.title}`}>Verify Code</h2>
                <p className={`${Styles.subtitle}`}>
                    Please enter the verification code that we've sent to {email || 'your email'}.
                </p>

                <div className={`${Styles.form}`}>
                    <div className={`${Styles.inputGroup} ${error ? Styles.error : ''}`}>
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                type="text"
                                value={digit}
                                maxLength="1"
                                ref={(el) => (inputRefs.current[index] = el)}
                                onChange={(e) => handleChange(index, e)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                className={error ? Styles.errorInput : ''}
                            />
                        ))}
                    </div>
                    {error && <span className={Styles.errorMessage}>{error}</span>}
                    <button onClick={handleOtpVerify} className={`${Styles.verifyButton}`} disabled={loading}>{loading ? <AiOutlineLoading /> : "Verify"}</button>
                </div>
            </div>

            <div className={`${Styles.backToLogin}`}>
                <a href="/login"><IoArrowBack /> Back to log in</a>
            </div>
        </div>
    );
};

export default OtpVerify;
